import React, { ReactNode, useEffect, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { Roles } from '@/constants/roles';
import { Routes } from '@/constants/routes';
import { useRole } from '@/hooks/useRole';
import { useGetUserQuery } from '@/api';
import { useIsMerchant } from '@/hooks/useIsMerchant';
import { OverlayProvider } from 'react-aria';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { capitalize } from 'lodash';
import useUpdatedTermsModal from '@/hooks/useUpdatedTermsModal';
import { useCheckCapabilities } from '@/api/queryHooks/useMerchantCryptoController';
import { DropdownDataProps, Navigation, NavigationDataProps, Popup, IconType } from '../common';
import styles from './Layout.module.css';
import { TermsAndPrivacy } from '../common/popups';
import { TermsAndPrivacyUpdated } from '../common/popups/TermsAndPrivacy/TermsAndPrivacyUpdated';

interface LayoutProps {
  children?: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const { width } = useWindowSize();
  const role = useRole();
  const isMerchant = useIsMerchant();
  const { data: user } = useGetUserQuery({
    onSuccess: (data) => {
      datadogRum.setUser({ id: data.websocketId });
      datadogRum.setGlobalContextProperty('x-trace-user-id', data.websocketId);
      datadogLogs.setGlobalContextProperty('x-trace-user-id', data.websocketId);
    },
  });

  const { data: capabilities } = useCheckCapabilities();

  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const { handleModalClose: handleUpdatedTermsModalClose, isModalShown: isUpdatedTermsModalShown } =
    useUpdatedTermsModal(String(user?.createdAt));

  const NAVIGATION_DATA: NavigationDataProps[] = [
    {
      href: Routes.OVERVIEW,
      title: 'Overview',
      icon: 'overview',
    },
    {
      href: Routes.BUY_CRYPTO,
      title: 'Buy Crypto',
      icon: 'transactionLimit',
    },
    {
      href: width < 1000 ? Routes.SELL_CRYPTO : undefined,
      title: 'Sell Crypto',
      icon: 'sell',
      chevron: true,
    },
    {
      href: isMerchant ? Routes.T_MERCHANT_PAYMENTS : Routes.TRANSACTIONS,
      title: 'Transactions',
      icon: 'transactions',
    },
    {
      href: Routes.TUTORIAL,
      title: 'Tutorial',
      icon: 'helpCircled',
    },
  ];

  const ACCOUNT_DROPDOWN_DATA: DropdownDataProps[] = [
    {
      href: Routes.PROFILE_DETAILS,
      title: 'Profile Details',
      icon: 'people',
    },
    {
      href: Routes.API_TOKEN,
      title: 'API Token',
      icon: 'apiToken',
    },
    ...(isMerchant
      ? [
          {
            href: Routes.WEBHOOK_HISTORY,
            title: 'Webhook History',
            icon: 'bills' as IconType,
          },
        ]
      : []),

    ...(isMerchant && capabilities?.fxCalculator === true
      ? [
          {
            href: Routes.FX_CALCULATOR,
            title: 'FX Calculator',
            icon: 'usd' as IconType,
          },
        ]
      : []),
    {
      href: Routes.TUTORIAL,
      title: 'Tutorial',
      icon: 'helpCircled',
    },
    {
      href: '',
      title: 'Download App',
      icon: 'downloadApp',
    },
  ];

  const SELL_CRYPTO_DROPDOWN_DATA: DropdownDataProps[] = [
    {
      href: Routes.SELL_CRYPTO,
      title: 'Bank Transfer',
      icon: 'bankTransfer',
    },
    {
      href: `${Routes.SELL_CRYPTO}/bill-payments`,
      title: 'Bill Payments',
      icon: 'bills',
    },
    {
      href: `${Routes.SELL_CRYPTO}/gift-cards`,
      title: 'Gift Cards',
      icon: 'gift',
    },
  ];

  const personalDropdownData = ACCOUNT_DROPDOWN_DATA.filter((item) => {
    return item.href === Routes.PROFILE_DETAILS || item.href === Routes.TUTORIAL;
  });

  useEffect(() => {
    if (user?.termsAndConditionsAgreed !== undefined && user?.termsAndConditionsAgreed !== true) {
      setIsTermsModalOpen(true);
    }
  }, [user?.termsAndConditionsAgreed]);

  return (
    <div className={styles.layout}>
      <Navigation
        className={styles.nav}
        account={{ role, placeholder: 'Acc', src: user?.picture }}
        navigationLinkList={NAVIGATION_DATA}
        dropdownLinkList={role === Roles.BUSINESS ? ACCOUNT_DROPDOWN_DATA : personalDropdownData}
        sellCryptoDropdownList={SELL_CRYPTO_DROPDOWN_DATA}
      />
      <main className={styles.main}>{children}</main>
      <OverlayProvider>
        <Popup isOpen={isTermsModalOpen}>
          <TermsAndPrivacy onClose={() => setIsTermsModalOpen(false)} />
        </Popup>

        <Popup isOpen={isUpdatedTermsModalShown}>
          <TermsAndPrivacyUpdated name={capitalize(user?.firstName)} onClose={() => handleUpdatedTermsModalClose()} />
        </Popup>
      </OverlayProvider>
    </div>
  );
};

export default Layout;
